import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, ChevronRight, ChevronDown } from "lucide-react";
import AdminOrderDetail from "@/components/AdminOrderDetail";
import { formatOrderDate } from "@/lib/applicants";

const STATUS_BADGES = {
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const STATUS_LABELS = { pending_payment: "Pending", in_progress: "In Progress", completed: "Completed" };

const isPaid = (o) => o.payment_status === "paid";
const isActiveExpress = (o) => o.delivery_type === "express" && isPaid(o) && o.status !== "completed";
const expressDeadline = (o) => {
  const paidAt = o.paid_at || o.updated_at || o.created_at;
  return paidAt ? new Date(paidAt).getTime() + 3 * 3600000 : null;
};
const fmtRemaining = (ms) => {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

function ExpressCountdownCell({ order, now }) {
  const dl = expressDeadline(order);
  if (!dl) return null;
  const rem = dl - now;
  if (rem <= 0) {
    return (
      <div className="mt-0.5 text-xs font-bold text-red-600" data-testid={`express-overdue-${order.id}`}>
        ⚠ OVERDUE by {fmtRemaining(-rem)}
      </div>
    );
  }
  const cls = rem < 30 * 60000
    ? "text-red-600 font-bold"
    : rem < 90 * 60000
      ? "text-amber-600 font-semibold"
      : "text-[#94a3b8]";
  return (
    <div className={`mt-0.5 text-xs ${cls}`} data-testid={`express-countdown-${order.id}`}>
      ⏱ {fmtRemaining(rem)} left
    </div>
  );
}

export default function AdminOrders({ filterStatus = null, title = "All Orders" }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Default view = paid orders only. Filter values: all (paid), pending_payment (paid+fulfillment-pending),
  // in_progress, completed, unpaid (payment_status != paid).
  const [statusFilter, setStatusFilter] = useState(filterStatus || "all");
  const [expanded, setExpanded] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setStatusFilter(filterStatus || "all"); }, [filterStatus]);

  // Counts (paid only, except unpaid tab)
  const counts = useMemo(() => {
    const paid = orders.filter(isPaid);
    return {
      all: paid.length,
      pending_payment: paid.filter((o) => o.status === "pending_payment").length,
      in_progress: paid.filter((o) => o.status === "in_progress").length,
      completed: paid.filter((o) => o.status === "completed").length,
      unpaid: orders.filter((o) => !isPaid(o)).length,
    };
  }, [orders]);

  const filtered = useMemo(() => {
    let list = orders;
    if (statusFilter === "unpaid") {
      list = list.filter((o) => !isPaid(o));
    } else {
      // Default + fulfillment tabs only consider PAID orders
      list = list.filter(isPaid);
      if (statusFilter !== "all") list = list.filter((o) => o.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((o) =>
        (o.full_name || "").toLowerCase().includes(q) ||
        (o.email || "").toLowerCase().includes(q) ||
        (o.destination_country || "").toLowerCase().includes(q) ||
        o.id.includes(q)
      );
    }
    if (statusFilter === "in_progress") {
      // Active express orders first, most urgent deadline on top
      list = [...list].sort((a, b) => {
        const da = isActiveExpress(a) ? (expressDeadline(a) ?? Infinity) : Infinity;
        const db = isActiveExpress(b) ? (expressDeadline(b) ?? Infinity) : Infinity;
        return da - db;
      });
    }
    return list;
  }, [orders, statusFilter, search]);

  const showFulfillmentColumn = statusFilter !== "unpaid";

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#2563eb] font-semibold">Orders</div>
          <h1 className="mt-1 font-display font-bold text-3xl text-[#0f1b2d]">{title}</h1>
          <p className="mt-1 text-xs text-[#64748b]">
            {statusFilter === "unpaid"
              ? "Abandoned checkouts and orders awaiting payment. Not counted in fulfillment stats."
              : "Default view shows paid orders only. Switch to Unpaid to see abandoned/pending checkouts."}
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search"
            placeholder="Search by name, email, country, order ID…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#e2e8f0] rounded-md outline-none focus:border-[#2563eb] bg-white"
          />
        </div>
        {!filterStatus && (
          <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
            {[
              { v: "all", l: "All", c: counts.all },
              { v: "pending_payment", l: "Pending", c: counts.pending_payment },
              { v: "in_progress", l: "In Progress", c: counts.in_progress },
              { v: "completed", l: "Completed", c: counts.completed },
              { v: "unpaid", l: "Unpaid", c: counts.unpaid, accent: true },
            ].map((s) => {
              const active = statusFilter === s.v;
              const accent = s.accent;
              return (
                <button
                  key={s.v}
                  onClick={() => setStatusFilter(s.v)}
                  data-testid={`filter-${s.v}`}
                  className={`px-3 py-1.5 text-sm rounded-md border transition inline-flex items-center gap-1.5 ${
                    active
                      ? (accent ? "bg-amber-600 text-white border-amber-600" : "bg-[#0f1b2d] text-white border-[#0f1b2d]")
                      : (accent ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100" : "bg-white text-[#0f1b2d] border-[#e2e8f0]")
                  }`}
                >
                  <span>{s.l}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                    active ? "bg-white/20" : (accent ? "bg-amber-100 text-amber-800" : "bg-[#f1f5f9] text-[#475569]")
                  }`}>{s.c}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 bg-white border border-[#e2e8f0] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f8fafc] text-xs uppercase tracking-wider text-[#64748b]">
              <tr>
                <th className="text-left px-4 py-3 w-8"></th>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Country</th>
                <th className="text-left px-4 py-3">Package</th>
                <th className="text-left px-4 py-3">Applicants</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Payment</th>
                {showFulfillmentColumn && <th className="text-left px-4 py-3">Fulfillment</th>}
                <th className="text-left px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={showFulfillmentColumn ? 10 : 9} className="px-4 py-10 text-center text-[#475569]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={showFulfillmentColumn ? 10 : 9} className="px-4 py-10 text-center text-[#475569]">No orders match this filter.</td></tr>
              ) : (
                filtered.map((o) => {
                  const open = expanded === o.id;
                  const paid = isPaid(o);
                  return (
                    <React.Fragment key={o.id}>
                      <tr
                        data-testid={`order-row-${o.id}`}
                        onClick={() => setExpanded(open ? null : o.id)}
                        className={`cursor-pointer hover:bg-[#f8fafc] border-t border-[#e2e8f0] ${open ? "bg-[#f8fafc]" : ""}`}
                      >
                        <td className="px-4 py-3">{open ? <ChevronDown className="w-4 h-4 text-[#475569]" /> : <ChevronRight className="w-4 h-4 text-[#475569]" />}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#475569]">#{o.id.slice(-8)}</td>
                        <td className="px-4 py-3">
                          <div className="text-[#0f1b2d] font-medium">{o.full_name}</div>
                          <div className="text-xs text-[#64748b]">{o.email}</div>
                        </td>
                        <td className="px-4 py-3 text-[#0f1b2d]">{o.destination_country}</td>
                        <td className="px-4 py-3 text-[#0f1b2d]" data-testid={`row-package-${o.id}`}>
                          <span className="inline-flex items-center gap-1.5 flex-wrap">
                            {o.order_type === "custom" ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-[#eff6ff] text-[#2563eb] border border-[#bfdbfe]">CUSTOM</span>
                            ) : (
                              o.package_name
                            )}
                            {isActiveExpress(o) && (
                              <span data-testid={`express-badge-${o.id}`} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300">
                                ⚡ EXPRESS
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#0f1b2d]" data-testid={`row-applicants-${o.id}`}>{o.num_applicants} {o.num_applicants === 1 ? "person" : "persons"}</td>
                        <td className="px-4 py-3 font-display font-bold text-[#0f1b2d]">${Math.round(o.amount)}</td>
                        <td className="px-4 py-3">
                          {paid ? (
                            <span data-testid={`payment-badge-${o.id}`} className="text-xs font-semibold px-2 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                              Paid
                            </span>
                          ) : (
                            <span data-testid={`payment-badge-${o.id}`} className="text-xs font-semibold px-2 py-1 rounded-full border bg-slate-100 text-slate-600 border-slate-200">
                              Unpaid
                            </span>
                          )}
                        </td>
                        {showFulfillmentColumn && (
                          <td className="px-4 py-3">
                            {paid && STATUS_LABELS[o.status] ? (
                              <span data-testid={`fulfillment-badge-${o.id}`} className={`text-xs font-semibold px-2 py-1 rounded-full border ${STATUS_BADGES[o.status]}`}>
                                {STATUS_LABELS[o.status]}
                              </span>
                            ) : (
                              <span className="text-xs text-[#94a3b8]">—</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-xs text-[#475569]">
                          {formatOrderDate(o.created_at) || (o.created_at || "").slice(0, 10)}
                          {isActiveExpress(o) && <ExpressCountdownCell order={o} now={now} />}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={showFulfillmentColumn ? 10 : 9} className="p-0">
                            <AdminOrderDetail order={o} onUpdate={load} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
